import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Typography,
    Collapse,
    Divider,
    Alert,
    LinearProgress,
    Tooltip
} from '@mui/material';
import {
    Folder as FolderIcon,
    FolderOpen as FolderOpenIcon,
    Image as ImageIcon,
    VideoLibrary as VideoIcon,
    Delete as DeleteIcon,
    CloudUpload as UploadIcon,
    Visibility as PreviewIcon,
    ExpandLess,
    ExpandMore
} from '@mui/icons-material';
import { StorageItem } from '../../../common/interfaces';
import { DependencyInjectionContext } from '../../../contexts/dependency-injection-context';

export type FileType = 'image' | 'video' | 'all';

interface FileManagerProps {
    open: boolean;
    onClose: () => void;
    clientId: string;
    onFileSelect?: (filePath: string) => void;
    fileType?: FileType;
}

interface FileTreeNode {
    name: string;
    fullPath: string;
    isDirectory: boolean;
    children: FileTreeNode[];
}

interface FileManagerState {
    files: StorageItem[];
    loading: boolean;
    error: string | null;
    uploading: boolean;
    previewFile: string | null;
    previewImageUrl: string | null;
    loadingPreview: boolean;
}

export const FileManager: React.FC<FileManagerProps> = ({
    open,
    onClose,
    clientId,
    onFileSelect,
    fileType = 'all'
}) => {
    const { formsApi } = useContext(DependencyInjectionContext);
    const [state, setState] = useState<FileManagerState>({
        files: [],
        loading: false,
        error: null,
        uploading: false,
        previewFile: null,
        previewImageUrl: null,
        loadingPreview: false
    });
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
    const [fileTree, setFileTree] = useState<FileTreeNode[]>([]);

    useEffect(() => {
        if (open) {
            loadFiles();
        }
    }, [open, clientId]);

    useEffect(() => {
        if (state.files.length > 0) {
            buildFileTree();
        }
    }, [state.files]);

    // Cleanup object URL when component unmounts or preview changes
    useEffect(() => {
        return () => {
            if (state.previewImageUrl) {
                URL.revokeObjectURL(state.previewImageUrl);
            }
        };
    }, [state.previewImageUrl]);

    const loadFiles = async () => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const files = await formsApi.fetchWelcomeBoardFiles(clientId);
            setState(prev => ({ ...prev, files, loading: false }));
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: `Failed to load files: ${error}`,
                loading: false
            }));
        }
    };

    const buildFileTree = () => {
        const root: FileTreeNode[] = [];
        const pathMap = new Map<string, FileTreeNode>();

        state.files.forEach(file => {
            const pathParts = file.path.split('/');
            let currentPath = '';

            pathParts.forEach((part, index) => {
                const previousPath = currentPath;
                currentPath = currentPath ? `${currentPath}/${part}` : part;
                const isLastPart = index === pathParts.length - 1;

                if (!pathMap.has(currentPath)) {
                    const node: FileTreeNode = {
                        name: part,
                        fullPath: currentPath,
                        isDirectory: !isLastPart,
                        children: []
                    };

                    pathMap.set(currentPath, node);

                    if (previousPath) {
                        const parentNode = pathMap.get(previousPath);
                        if (parentNode) {
                            parentNode.children.push(node);
                        }
                    } else {
                        root.push(node);
                    }
                }
            });
        });

        setFileTree(root);
    };

    const toggleFolder = (folderPath: string) => {
        setExpandedFolders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(folderPath)) {
                newSet.delete(folderPath);
            } else {
                newSet.add(folderPath);
            }
            return newSet;
        });
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setState(prev => ({ ...prev, uploading: true, error: null }));

        try {
            for (const file of Array.from(files)) {
                const isValidImage = file.type.startsWith('image/');
                const isValidVideo = file.type.startsWith('video/');

                if (fileType === 'image' && !isValidImage) {
                    throw new Error(`${file.name} is not a valid image file`);
                }

                if (fileType === 'video' && !isValidVideo) {
                    throw new Error(`${file.name} is not a valid video file`);
                }

                if (fileType === 'all' && !isValidImage && !isValidVideo) {
                    throw new Error(`${file.name} is not a valid image or video file`);
                }

                await formsApi.uploadWelcomeBoardFile(file, file.name, clientId);
            }

            await loadFiles();
            setState(prev => ({ ...prev, uploading: false }));
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: `Failed to upload files: ${error}`,
                uploading: false
            }));
        }

        // Reset input
        event.target.value = '';
    };

    const handleFileDelete = async (filePath: string) => {
        if (!window.confirm(`Are you sure you want to delete ${filePath}?`)) {
            return;
        }

        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            await formsApi.deleteWelcomeBoardFile(filePath, clientId);
            await loadFiles();
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: `Failed to delete file: ${error}`,
                loading: false
            }));
        }
    };

    const handlePreview = async (filePath: string) => {
        setState(prev => ({ ...prev, previewFile: filePath, loadingPreview: true, error: null }));

        try {
            const blob = await formsApi.fetchWelcomeBoardFile(filePath, clientId);
            const objectUrl = URL.createObjectURL(blob);
            setState(prev => ({ ...prev, previewImageUrl: objectUrl, loadingPreview: false }));
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: `Failed to load preview: ${error}`,
                loadingPreview: false
            }));
        }
    };

    const getFileUrl = (filePath: string) => {
        return formsApi.getWelcomeBoardFileUrl(filePath, clientId);
    };

    const isImageFile = (fileName: string) => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
        return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
    };

    const isVideoFile = (fileName: string) => {
        const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v'];
        return videoExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
    };

    const isMediaFile = (fileName: string) => {
        return isImageFile(fileName) || isVideoFile(fileName);
    };

    const shouldShowFile = (fileName: string) => {
        if (fileType === 'all') return isMediaFile(fileName);
        if (fileType === 'image') return isImageFile(fileName);
        if (fileType === 'video') return isVideoFile(fileName);
        return false;
    };

    const renderFileTree = (nodes: FileTreeNode[], depth = 0) => {
        return nodes
            .filter(node => node.isDirectory || shouldShowFile(node.name))
            .map(node => {
                const isImage = isImageFile(node.name);
                const isVideo = isVideoFile(node.name);
                const isMedia = isImage || isVideo;

                return (
                    <div key={node.fullPath}>
                        <ListItem sx={{ pl: depth * 2 + 1 }}>
                            <ListItemIcon>
                                {node.isDirectory ? (
                                    expandedFolders.has(node.fullPath) ? (
                                        <FolderOpenIcon />
                                    ) : (
                                        <FolderIcon />
                                    )
                                ) : isVideo ? (
                                    <VideoIcon color="primary" />
                                ) : isImage ? (
                                    <ImageIcon color="primary" />
                                ) : (
                                    <ImageIcon color="disabled" />
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary={node.name}
                                onClick={() => {
                                    if (node.isDirectory) {
                                        toggleFolder(node.fullPath);
                                    } else if (onFileSelect) {
                                        onFileSelect(node.fullPath);
                                    }
                                }}
                                sx={{ cursor: 'pointer' }}
                            />
                            {node.isDirectory && (
                                <IconButton onClick={() => toggleFolder(node.fullPath)} size="small">
                                    {expandedFolders.has(node.fullPath) ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            )}
                            {!node.isDirectory && isMedia && (
                                <>
                                    <Tooltip title="Preview">
                                        <IconButton
                                            onClick={() => handlePreview(node.fullPath)}
                                            size="small"
                                            color="primary"
                                        >
                                            <PreviewIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            onClick={() => handleFileDelete(node.fullPath)}
                                            size="small"
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                        </ListItem>
                        {node.isDirectory && expandedFolders.has(node.fullPath) && (
                            <Collapse in={true} timeout="auto" unmountOnExit>
                                {renderFileTree(node.children, depth + 1)}
                            </Collapse>
                        )}
                    </div>
                );
            });
    };

    const getUploadButtonText = () => {
        if (fileType === 'image') return 'Upload Images';
        if (fileType === 'video') return 'Upload Videos';
        return 'Upload Files';
    };

    const getAcceptAttribute = () => {
        if (fileType === 'image') return 'image/*';
        if (fileType === 'video') return 'video/*';
        return 'image/*,video/*';
    };

    const getEmptyStateText = () => {
        if (fileType === 'image') return 'No images found. Upload some images to get started.';
        if (fileType === 'video') return 'No videos found. Upload some videos to get started.';
        return 'No files found. Upload some images or videos to get started.';
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        File Manager
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={<UploadIcon />}
                            disabled={state.uploading}
                        >
                            {getUploadButtonText()}
                            <input
                                type="file"
                                hidden
                                multiple
                                accept={getAcceptAttribute()}
                                onChange={handleFileUpload}
                            />
                        </Button>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ minHeight: 400, maxHeight: 600 }}>
                    {state.loading && <LinearProgress sx={{ mb: 2 }} />}
                    {state.uploading && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Uploading files...
                        </Alert>
                    )}
                    {state.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {state.error}
                        </Alert>
                    )}
                    
                    {fileTree.length > 0 ? (
                        <List dense>
                            {renderFileTree(fileTree)}
                        </List>
                    ) : !state.loading && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                                {getEmptyStateText()}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog
                open={!!state.previewFile}
                onClose={() => {
                    if (state.previewImageUrl) {
                        URL.revokeObjectURL(state.previewImageUrl);
                    }
                    setState(prev => ({
                        ...prev,
                        previewFile: null,
                        previewImageUrl: null,
                        loadingPreview: false
                    }));
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Preview: {state.previewFile}</DialogTitle>
                <DialogContent>
                    {state.loadingPreview && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <LinearProgress sx={{ mb: 2 }} />
                            <Typography>Loading preview...</Typography>
                        </Box>
                    )}
                    {state.previewImageUrl && !state.loadingPreview && (
                        <Box sx={{ textAlign: 'center' }}>
                            {state.previewFile && isVideoFile(state.previewFile) ? (
                                <video
                                    src={state.previewImageUrl}
                                    controls
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '500px',
                                        objectFit: 'contain'
                                    }}
                                />
                            ) : (
                                <img
                                    src={state.previewImageUrl}
                                    alt={state.previewFile || ''}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '500px',
                                        objectFit: 'contain'
                                    }}
                                />
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        if (state.previewImageUrl) {
                            URL.revokeObjectURL(state.previewImageUrl);
                        }
                        setState(prev => ({
                            ...prev,
                            previewFile: null,
                            previewImageUrl: null,
                            loadingPreview: false
                        }));
                    }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};